import { fetcher } from '../utils/fetcher';

export interface NotificationCounts {
  tasks: number;
  overdue_tasks: number;
  unassigned_tasks: number;
  pending_registrations: number;
  total: number;
}

/**
 * Fetch notification counts from backend APIs
 */
export const fetchNotificationCounts = async (): Promise<NotificationCounts> => {
  try {
    const [tasks, users] = await Promise.all([
      fetcher('/api/v1/tasks/read').catch(() => []),
      fetcher('/api/v1/users/read').catch(() => []),
    ]);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const overdue_tasks = tasks.filter((task: any) => {
      if (!task.due_date) return false;
      const dueDate = new Date(task.due_date);
      return dueDate < today && task.status !== 'completed';
    }).length;

    const unassigned_tasks = tasks.filter((task: any) => !task.assigned_to && task.status !== 'completed').length;

    const pending_registrations = users.filter((user: any) => user.status === 'pending').length;

    const total = overdue_tasks + unassigned_tasks + pending_registrations;

    return {
      tasks: overdue_tasks + unassigned_tasks,
      overdue_tasks,
      unassigned_tasks,
      pending_registrations,
      total,
    };
  } catch (error) {
    console.error('Failed to fetch notification counts:', error);
    return {
      tasks: 0,
      overdue_tasks: 0,
      unassigned_tasks: 0,
      pending_registrations: 0,
      total: 0,
    };
  }
};

/**
 * WebSocket connection for real-time notifications
 * TODO: Implement when backend WebSocket support is added
 */
export class NotificationWebSocket {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  constructor(
    private url: string,
    private onMessage: (data: any) => void,
    private onError?: (error: Event) => void
  ) {}

  connect() {
    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log('Notification WebSocket connected');
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.onMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.onError?.(error);
      };

      this.ws.onclose = () => {
        console.log('WebSocket closed, attempting to reconnect...');
        this.reconnect();
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
    }
  }

  private reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    setTimeout(() => {
      console.log(`Reconnection attempt ${this.reconnectAttempts}`);
      this.connect();
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  send(data: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }
}
